/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css' // Import the Apple Pay button style.
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react' // Fetch the basket and use Salesforce Commerce APIs.
import AdyenExpressCheckoutProvider from '../../../contexts/adyen-express-checkout-context' // Import the Adyen Apple Pay Express component.
import ApplePayExpress from './applePayExpress'
import useMultiSite from '../../../hooks/use-multi-site' // Import Salesforce React hooks to get the locale and site name for the shopper session.
import {useCurrentBasket} from '../../../hooks/use-current-basket'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useSiteType from '../../../hooks/use-site-type'
import useShipToCountry from '../../../hooks/use-ship-to-country'
import {useGiftCardLogic} from '../../../hooks/use-gift-card-logic'

/**
 * CartApplePayCta is a React functional component that conditionally renders an Apple Pay button
 * for express checkout based on the user's locale and site type. It utilizes several hooks to
 * gather necessary data and state, such as the current basket, customer ID, access token, and
 * navigation functions.
 *
 * The component determines whether to display the Apple Pay button by checking if the user's
 * country code is 'FR' or if the site is within the EU and not an outlet. If these conditions
 * are met, the Apple Pay button is rendered within the AdyenExpressCheckoutProvider, which
 * provides the necessary context and authentication token for the Apple Pay express checkout
 * process.
 *
 * Hooks and Contexts Used:
 * - useCurrentBasket: Retrieves the current shopping basket data.
 * - useCustomerId: Fetches the current customer's ID.
 * - useAccessToken: Provides a method to obtain an access token when ready.
 * - useNavigation: Supplies navigation functions for routing.
 * - useMultiSite: Offers locale and site information.
 * - useSiteType: Determines if the site is within the EU or is an outlet.
 *
 * Effects:
 * - An effect is used to asynchronously fetch and set the authentication token once it is ready.
 *
 * Returns:
 * - A JSX element containing the Apple Pay button wrapped in the AdyenExpressCheckoutProvider if
 *   the conditions for displaying the button are met. Otherwise, it returns an empty fragment.
 */
const CartApplePayCta = () => {
    const {shipToCountry} = useShipToCountry()
    const {data: basket} = useCurrentBasket()
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const navigate = useNavigation()
    const {locale, site} = useMultiSite()
    const [isApplePayActive, setIsApplePayActive] = useState(false)
    let countryCode = locale.id.split('-')[1]
    countryCode = countryCode.toUpperCase()
    const {isUS, isOutlet} = useSiteType()
    const {checkIfThereAnyGCInBasket} = useGiftCardLogic()
    const [authToken, setAuthToken] = useState()

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])

    useEffect(() => {
        /*
            If the shipToCountry is FR or US, then Apple Pay is active.
            If there are any gift cards in the basket, then Apple Pay is inactive. 
        */
        const isShipToCountryApplicable = shipToCountry.id === 'FR' || shipToCountry.id === 'US'
        let isThereAnyGiftCardInBasket = false
        if (basket) {
            isThereAnyGiftCardInBasket = checkIfThereAnyGCInBasket(basket)
        }
        setIsApplePayActive(!isThereAnyGiftCardInBasket && isShipToCountryApplicable)
    }, [basket, shipToCountry])

    return isApplePayActive ? (
        <Box mt="8" css={[CartApplePayCta.css]}>
            <AdyenExpressCheckoutProvider
                authToken={authToken}
                customerId={customerId}
                locale={locale}
                site={site}
                basket={basket}
                navigate={navigate}
            >
                <ApplePayExpress />
            </AdyenExpressCheckoutProvider>
        </Box>
    ) : null
}

CartApplePayCta.css = css`
    .adyen-checkout__applepay__button {
        width: 100%;
    }
`

export default CartApplePayCta
